// rename this to VKT instead of utilization...
import { zipWith } from 'lodash';
import { createSelector } from 'reselect';
import { IOwnershipMode } from './../../types/city';
import { getComponentKey, getSharingMode, PROJECTION_START_YEAR } from './../index';

import { getProjectedModalSplitByOwnerAndTypeSeries } from '../modalSplit';
import { getPassengerDistanceTraveledSeriesMapping } from '../passengerDistanceTraveled';

// calculate vehicle usage
import { getProjectedPopulationSeries } from '../population';

import occupancyMap from './table';


// Billion Passenger Kilometers Traveled
export const getBPKMSeries = createSelector(
  [getProjectedPopulationSeries, getPassengerDistanceTraveledSeriesMapping, getComponentKey],
  (populationSeries, perCapitaDistance, scenarioKey): number[] => {

    return zipWith(perCapitaDistance[scenarioKey] as number[], populationSeries, (distance, population) => {
      return distance * population;
    });
  }
)

// Derived stuff...
// This is the top portion of the equation: B:51 to B:58 of Sheet:MarketSize
export const getVKTNumerator = createSelector(
  [getBPKMSeries, getProjectedModalSplitByOwnerAndTypeSeries],
  (bpkmSeries, modalSplitSeries) => {
    return zipWith(bpkmSeries as number[], modalSplitSeries, (bpkm, modalSplit) => {
      const factor = bpkm / Math.pow(10, 9); // adjust for billion kilometers

      return {
        autoRickshaw: {
          commercial: modalSplit.autoRickshaw.commercial * factor,
        },
        car: {
          commercial: modalSplit.car.commercial * factor,
          private: modalSplit.car.private * factor,
          public: modalSplit.car.public * factor,
        },
        publicTransit: {
          bus: modalSplit.publicTransit.bus * factor,
          metro: modalSplit.publicTransit.metro * factor,
        },
        twoWheel: {
          commercial: modalSplit.twoWheel.commercial * factor,
          private: modalSplit.twoWheel.private * factor,
        },
      }
    });
  }
)

// These metrics are SCENARIO SPECIFIC
export const getVKTByOwner = createSelector(
  [getVKTNumerator, getSharingMode],
  (numeratorSeries, sharingMode) => {

    const startYear = PROJECTION_START_YEAR;
    const utilization = occupancyMap[sharingMode];

    const series = numeratorSeries.map((numerator: IOwnershipMode, i: number) => {
      const year = startYear + i;
      const yearMap = utilization[year];
      // each of the commercial items get adjusted by a growth factor of 1!
      return {
        autoRickshaw: {
          commercial: numerator.autoRickshaw.commercial / (yearMap.autoRickshaw.commercial - 1),
        },
        car: {
          commercial: numerator.car.commercial / (yearMap.car.commercial - 1),
          private: (numerator.car.private) / yearMap.car.private,
          public: numerator.car.public / yearMap.car.public,
        },
        publicTransit: {
          bus: numerator.publicTransit.bus / yearMap.publicTransit.bus,
          metro: numerator.publicTransit.metro / yearMap.publicTransit.metro,
        },
        twoWheel: {
          commercial: numerator.twoWheel.commercial / (yearMap.twoWheel.commercial - 1),
          private: numerator.twoWheel.private / yearMap.twoWheel.private,
        },
      }
    });
    return series;
  }
)

export const getVKTTotalFromMapping = (mapping: IOwnershipMode): number => {
  const subtotals = [
    mapping.autoRickshaw.commercial,
    mapping.car.commercial, // intentionally skip car public
    mapping.car.private,
    mapping.publicTransit.metro,
    mapping.publicTransit.bus,
    mapping.twoWheel.commercial,
    mapping.twoWheel.private,
  ];

  const subtotal = subtotals.reduce((accum, val) => accum + val, 0);
  return subtotal;
}

export const makeGetVKTTotal = () => {
  return createSelector(
    [getVKTByOwner],
    (vktSeries): number => {
      const endGame = vktSeries.slice(-1)[0]; // guard against secondary - todo, change to number 20?
      const value = endGame ? getVKTTotalFromMapping(endGame) : 0;
      return value;
    }
  );
}

export const getVKTTotal = makeGetVKTTotal();

export const getVKTSeries = createSelector(
  [getVKTByOwner],
  (vktSeries): number[] => {
    return vktSeries.map(getVKTTotalFromMapping);
  }
);



// WEBPACK FOOTER //
// ./src/store/selectors/vehicleDistanceTraveled/index.ts